import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { readingsByPerson, useAcknowledgements } from '../data/acknowledgements.ts'
import { say, useDocuments, useDocumentVersions } from '../data/plan.ts'
import { useRoster } from '../data/planUi.ts'
import { formatLocal } from '../lib/time.ts'
import { Pill } from '../ui/components.tsx'

const READ_TONE = { read: 'green', older: 'amber', unread: 'gray' } as const
/** M16-I2: who read the procedures that ask for a reading ("demander une lecture", Planning › Réglages › Procédures), person by person — the team of today
 *  (the roster, by name: nothing is ordered by how much a person read). Each person a 56-px fold with "lues : 2 / 3"; inside, each procedure's version in
 *  force read (the day), an earlier version only, or not read. From the device's copy (both profiles mirror the readings). A reading is not a competence:
 *  nothing here gates a job, and the page says so. */
export function Readings() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const roster = useRoster(); const docs = useDocuments(); const versions = useDocumentVersions(); const acks = useAcknowledgements()
  const rows = useMemo(() => (roster && docs && versions && acks
    ? readingsByPerson(roster.map((p) => ({ id: p.id, name: p.name })), docs, versions, acks, (v) => say(v.title, lang), lang) : undefined), [roster, docs, versions, acks, lang])
  const asked = rows?.[0]?.items.length ?? 0
  const day = (iso: string | null) => (iso ? formatLocal(iso, site.timezone, 'dd/MM/yyyy') : '')
  return (
    <section id="readings" className="flex scroll-mt-20 flex-col gap-3" data-testid="qualif-readings">
      <h2 className="text-xl font-bold">{t('ack.qualif.title')}</h2>
      <p className="meta" data-testid="readings-rule">{t('ack.notCompetence')}</p>
      {!rows ? <p>{t('common.loading')}</p> : asked === 0 || rows.length === 0 ? <p className="opacity-70" data-testid="readings-none">{t('ack.qualif.none')}</p> : (
        <ul className="flex flex-col gap-2" data-testid="readings-list">
          {rows.map((r) => (
            <li key={r.person_id} className="card !py-1" data-testid="readings-person" data-person={r.person_id} data-read={r.read} data-unread={r.unread}>
              <details>
                <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-2 font-semibold [&::-webkit-details-marker]:hidden">
                  <span className="min-w-0">{r.name}</span><span className="meta shrink-0">{t('ack.qualif.counts', { read: r.read, total: r.items.length })}</span>
                </summary>
                <ul className="flex flex-col gap-2 pb-2">
                  {r.items.map((i) => (
                    <li key={i.document_id} className="flex flex-wrap items-center justify-between gap-2" data-testid="readings-item" data-state={i.state} data-document={i.document_id}>
                      <span className="min-w-0">{say(i.title, lang)} <span className="meta">· {t('plan.version', { n: i.version_no })}</span></span>
                      <Pill tone={READ_TONE[i.state]}>{i.state === 'read' ? t('ack.qualif.read', { day: day(i.read_at) }) : i.state === 'older' ? t('ack.qualif.older', { n: i.read_version_no, day: day(i.read_at) }) : t('ack.qualif.unread')}</Pill>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
